import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getUsers,
  editUser,
  deleteMember,
  getUserAssessments,
  getUserEnterpriseOptions,
  regenerateAdminReport,
  regenerateAdminReportDetailed,
} from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import Alert from '../../components/Alert';
import Button from '../../components/Button';

const ROLE_LABEL = {
  superadmin: '超级管理员',
  admin: '系统管理员',
  enterprise_admin: '企业管理员',
  instructor: '讲师',
};

const ROLE_COLOR = {
  personal_only: '#64748b',
  enterprise_user: '#0891b2',
  instructor: '#0d9488',
  enterprise_admin: '#2563eb',
  admin: '#7c3aed',
  superadmin: '#dc2626',
};

function getUserTypeKey(u) {
  if (u.role === 'personal') return u.enterprise_name ? 'enterprise_user' : 'personal_only';
  return u.role;
}

function getUserTypeLabel(u) {
  const key = getUserTypeKey(u);
  if (key === 'personal_only') return '个人用户';
  if (key === 'enterprise_user') return '企业用户';
  return ROLE_LABEL[u.role] || u.role;
}

const DEPTH_LABEL = { quick: '快速', standard: '标准', deep: '深度', enterprise: '企业' };
const STATUS_LABEL = { in_progress: '进行中', completed: '已完成', abandoned: '已放弃' };
const ROLE_EDITABLE = new Set(['personal', 'instructor', 'enterprise_admin']);

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isEnterpriseAdmin = currentUser?.role === 'enterprise_admin';
  const isSuperAdmin = currentUser?.role === 'superadmin';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterUserType, setFilterUserType] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [enterpriseOptions, setEnterpriseOptions] = useState([]);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [reportModal, setReportModal] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  // 报告弹窗内「重新生成」子面板：null=未打开；否则 { selId, variant, busy, msg, error }
  const [regen, setRegen] = useState(null);
  const PAGE_SIZE = 25;

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      if (filterUserType) params.user_type = filterUserType;
      const { data } = await getUsers(params);
      const rows = data.results ?? data;
      setUsers(rows);
      setTotal(data.count ?? rows.length);
    } catch (e) {
      setError(e.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, filterRole, filterUserType]); // eslint-disable-line

  useEffect(() => {
    if (!isSuperAdmin) return;
    getUserEnterpriseOptions()
      .then(({ data }) => setEnterpriseOptions(Array.isArray(data) ? data : []))
      .catch(() => setEnterpriseOptions([]));
  }, [isSuperAdmin]);

  const canEdit = (u) => {
    if (isSuperAdmin) return u.role !== 'superadmin' || u.id === currentUser?.id;
    if (isEnterpriseAdmin) return u.role === 'personal' && !!u.enterprise_name;
    return false;
  };

  const openEditModal = (u) => {
    setError('');
    setSuccess('');
    setEditModal(u);
    setEditForm({
      is_active: !!u.is_active,
      is_engineer_mode: !!u.is_engineer_mode,
      role: ROLE_EDITABLE.has(u.role) ? u.role : 'personal',
      enterprise_id: u.enterprise_id ? String(u.enterprise_id) : '',
    });
  };

  const closeEditModal = () => {
    setEditModal(null);
    setEditForm(null);
    setEditSaving(false);
  };

  const handleSaveEdit = async () => {
    if (!editModal || !editForm || !isSuperAdmin) return;

    const roleEditable = ROLE_EDITABLE.has(editModal.role);
    const clearingInstructorEnterprise =
      editModal.role === 'instructor' && editModal.enterprise_id && !editForm.enterprise_id;
    const roleNeedsEnterprise = editForm.role === 'instructor' || editForm.role === 'enterprise_admin';
    if (
      roleEditable &&
      roleNeedsEnterprise &&
      !editForm.enterprise_id &&
      !(editForm.role === 'instructor' && clearingInstructorEnterprise)
    ) {
      setError(editForm.role === 'enterprise_admin' ? '企业管理员必须先选择企业' : '讲师必须先选择企业');
      return;
    }

    const payload = {
      is_active: editForm.is_active,
      is_engineer_mode: editForm.is_engineer_mode,
    };
    if (roleEditable) {
      payload.role = editForm.role;
      payload.enterprise_id = editForm.enterprise_id ? Number(editForm.enterprise_id) : null;
    }

    setEditSaving(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await editUser(editModal.id, payload);
      setSuccess(data.auto_demoted ? '用户信息已更新，已自动撤销讲师身份' : (data.detail || '用户信息已更新'));
      closeEditModal();
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '保存失败');
    } finally {
      setEditSaving(false);
    }
  };

  const handleRemoveFromEnterprise = async () => {
    if (!editModal) return;
    if (!window.confirm(`确认将 ${editModal.email} 移出企业？该用户不会被删除，只会从本企业成员列表移除。`)) return;
    setEditSaving(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await editUser(editModal.id, { enterprise_id: null });
      setSuccess(data.detail || `已将 ${editModal.email} 移出企业`);
      closeEditModal();
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '移出企业失败');
    } finally {
      setEditSaving(false);
    }
  };

  const handleSoftDelete = async () => {
    if (!editModal) return;
    if (!window.confirm(`确认将 ${editModal.email} 移入回收站？该用户将无法登录，可由超管在回收站恢复。`)) return;
    setEditSaving(true);
    setError('');
    setSuccess('');
    try {
      await deleteMember(editModal.id);
      setSuccess(`已将 ${editModal.email} 移入回收站`);
      closeEditModal();
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '删除失败');
    } finally {
      setEditSaving(false);
    }
  };

  const openReportModal = async (u) => {
    setReportModal({ user: u, assessments: [] });
    setRegen(null);
    setReportLoading(true);
    try {
      const { data } = await getUserAssessments(u.id);
      setReportModal({ user: u, assessments: data });
    } catch {
      setReportModal({ user: u, assessments: null });
    } finally {
      setReportLoading(false);
    }
  };

  const closeReportModal = () => {
    setReportModal(null);
    setRegen(null);
  };

  // 触发重生（variant: 'rough' 基础版 / 'detailed' 专业版），成功后刷新测评列表。
  const doRegen = async () => {
    if (!regen || !regen.selId || regen.busy) return;
    setRegen(r => ({ ...r, busy: true, msg: '', error: '' }));
    try {
      if (regen.variant === 'detailed') {
        await regenerateAdminReportDetailed(regen.selId);
      } else {
        await regenerateAdminReport(regen.selId);
      }
      const label = regen.variant === 'detailed' ? '专业版' : '基础版';
      setRegen(r => ({
        ...r, busy: false,
        msg: `已触发${label}重新生成（测评 #${regen.selId}），约 1-2 分钟后完成。`,
      }));
      // 刷新列表，让状态/has_detailed 跟上
      if (reportModal?.user?.id) {
        try {
          const { data } = await getUserAssessments(reportModal.user.id);
          setReportModal(m => (m ? { ...m, assessments: data } : m));
        } catch { /* 列表刷新失败不影响重生已触发 */ }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || '重新生成失败，请重试';
      setRegen(r => ({ ...r, busy: false, error: detail }));
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const tableColSpan = 8;

  return (
    <AdminLayout title={isEnterpriseAdmin ? '成员管理' : '用户管理'}>
      {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
      {success && <Alert type="success" style={{ marginBottom: 12 }}>{success}</Alert>}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
          placeholder="搜索邮箱/姓名... (回车)"
          style={inputInlineStyle}
        />
        <select
          value={filterUserType || filterRole}
          onChange={e => {
            const v = e.target.value;
            setPage(1);
            if (v === 'enterprise_user' || v === 'personal_only') {
              setFilterRole('');
              setFilterUserType(v);
            } else {
              setFilterUserType('');
              setFilterRole(v);
            }
          }}
          style={selectInlineStyle}
        >
          <option value="">全部身份</option>
          <option value="personal_only">个人用户</option>
          <option value="enterprise_user">企业用户</option>
          <option value="instructor">讲师</option>
          <option value="enterprise_admin">企业管理员</option>
          {isSuperAdmin && <option value="admin">系统管理员</option>}
          {isSuperAdmin && <option value="superadmin">超级管理员</option>}
        </select>
        <span style={{ fontSize: 13, color: '#94a3b8', alignSelf: 'center', marginLeft: 4 }}>
          共 {total} 名用户
        </span>
        {isSuperAdmin && (
          <div style={{ marginLeft: 'auto' }}>
            <button onClick={() => navigate('/admin/users/recycle-bin')} style={secondaryButtonStyle}>
              用户回收站
            </button>
          </div>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['ID', '邮箱', '显示名', '角色', '企业', '状态', '注册时间', '操作'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={tableColSpan} style={emptyCellStyle}>加载中...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={tableColSpan} style={emptyCellStyle}>暂无数据</td></tr>
            ) : users.map(u => (
              <tr key={u.id} style={{
                borderBottom: '1px solid #f1f5f9',
                background: u.is_active ? '#fff' : '#fef2f2',
              }}>
                <td style={mutedTdStyle}>{u.id}</td>
                <td style={{ ...tdStyle, maxWidth: 220 }}>{u.email}</td>
                <td style={{ ...tdStyle, color: '#64748b' }}>{u.display_name || '-'}</td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    color: ROLE_COLOR[getUserTypeKey(u)] || '#64748b',
                    background: `${ROLE_COLOR[getUserTypeKey(u)] || '#64748b'}15`,
                  }}>
                    {getUserTypeLabel(u)}
                  </span>
                </td>
                <td style={{ ...tdStyle, color: '#64748b', fontSize: 12 }}>{u.enterprise_name || '-'}</td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      background: u.is_active ? '#f0fdf4' : '#fef2f2',
                      color: u.is_active ? '#16a34a' : '#dc2626',
                    }}>
                      {u.is_active ? '正常' : '已禁用'}
                    </span>
                    {u.is_engineer_mode && (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 600,
                        background: '#fef3c7',
                        color: '#d97706',
                      }}>
                        工程模式
                      </span>
                    )}
                  </div>
                </td>
                <td style={mutedTdStyle}>
                  {u.date_joined ? new Date(u.date_joined).toLocaleDateString('zh-CN') : '-'}
                </td>
                <td style={{ ...tdStyle, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => openReportModal(u)} style={smallBlueButtonStyle}>
                    查看报告
                  </button>
                  {canEdit(u) && (
                    <button onClick={() => openEditModal(u)} style={smallNeutralButtonStyle}>
                      编辑用户
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ ...pagerButtonStyle, cursor: page > 1 ? 'pointer' : 'not-allowed' }}>
            上一页
          </button>
          <span style={{ padding: '6px 14px', fontSize: 13, color: '#64748b' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ ...pagerButtonStyle, cursor: page < totalPages ? 'pointer' : 'not-allowed' }}>
            下一页
          </button>
        </div>
      )}

      {editModal && editForm && (
        <div onClick={closeEditModal} style={modalOverlayStyle}>
          <div onClick={e => e.stopPropagation()} style={modalCardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>编辑用户</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{editModal.email}</div>
              </div>
              <button onClick={closeEditModal} style={closeButtonStyle}>x</button>
            </div>

            <div style={summaryBoxStyle}>
              <div><b>{editModal.display_name || '-'}</b></div>
              <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
                {getUserTypeLabel(editModal)} / {editModal.enterprise_name || '未归属企业'}
              </div>
            </div>

            {isSuperAdmin ? (
              <>
                <div style={fieldGridStyle}>
                  <label style={checkRowStyle}>
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      disabled={editModal.id === currentUser?.id}
                      onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))}
                    />
                    启用账号
                  </label>
                  <label style={checkRowStyle}>
                    <input
                      type="checkbox"
                      checked={editForm.is_engineer_mode}
                      onChange={e => setEditForm(f => ({ ...f, is_engineer_mode: e.target.checked }))}
                    />
                    工程模式
                  </label>
                </div>

                {ROLE_EDITABLE.has(editModal.role) ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 16 }}>
                    <div>
                      <label style={labelStyle}>用户角色</label>
                      <select
                        value={editForm.role}
                        onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}
                        style={inputStyle}
                      >
                        <option value="personal">普通用户</option>
                        <option value="instructor">讲师</option>
                        <option value="enterprise_admin">企业管理员</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>企业归属</label>
                      <select
                        value={editForm.enterprise_id}
                        onChange={e => setEditForm(f => ({ ...f, enterprise_id: e.target.value }))}
                        style={inputStyle}
                      >
                        <option value="">不归属企业</option>
                        {enterpriseOptions.map(ent => (
                          <option key={ent.id} value={String(ent.id)}>
                            {ent.name} ({ent.code}){ent.is_active ? '' : ' - 未启用'}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div style={noticeStyle}>该角色不支持在此处调整角色或企业归属。</div>
                )}

                {editModal.id !== currentUser?.id && (
                  <div style={dangerBoxStyle}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#991b1b' }}>删除用户</div>
                      <div style={{ color: '#7f1d1d', fontSize: 12, marginTop: 4 }}>
                        删除后进入用户回收站，可由超管恢复或永久删除。
                      </div>
                    </div>
                    <Button variant="danger" onClick={handleSoftDelete} disabled={editSaving}>
                      移入回收站
                    </Button>
                  </div>
                )}

                <div style={modalFooterStyle}>
                  <Button variant="secondary" onClick={closeEditModal} disabled={editSaving}>取消</Button>
                  <Button onClick={handleSaveEdit} loading={editSaving}>保存</Button>
                </div>
              </>
            ) : (
              <>
                <div style={noticeStyle}>
                  企业管理员只能将本企业已注册成员移出企业；不会删除用户账号，也不会进入回收站。
                </div>
                <div style={modalFooterStyle}>
                  <Button variant="secondary" onClick={closeEditModal} disabled={editSaving}>取消</Button>
                  <Button variant="danger" onClick={handleRemoveFromEnterprise} loading={editSaving}>
                    移出企业
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {reportModal && (
        <div onClick={closeReportModal} style={modalOverlayStyle}>
          <div onClick={e => e.stopPropagation()} style={{ ...modalCardStyle, minWidth: 540, maxWidth: 680 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{reportModal.user.display_name || reportModal.user.email}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{reportModal.user.email}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {!regen
                  && Array.isArray(reportModal.assessments)
                  && reportModal.assessments.some(a => a.status === 'completed') && (
                  <button
                    onClick={() => setRegen({ selId: null, variant: 'rough', busy: false, msg: '', error: '' })}
                    style={regenEntryButtonStyle}
                  >
                    🔄 重新生成
                  </button>
                )}
                <button onClick={closeReportModal} style={closeButtonStyle}>x</button>
              </div>
            </div>
            {reportLoading ? (
              <div style={emptyCellStyle}>加载中...</div>
            ) : reportModal.assessments === null ? (
              <div style={{ ...emptyCellStyle, color: '#dc2626' }}>加载失败</div>
            ) : reportModal.assessments.length === 0 ? (
              <div style={emptyCellStyle}>该用户暂无测评记录</div>
            ) : regen ? (
              <RegenPanel
                assessments={reportModal.assessments}
                regen={regen}
                setRegen={setRegen}
                onConfirm={doRegen}
              />
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['ID', '模式', '深度', '状态', '得分', '等级', '完成时间', '操作'].map(h => (
                      <th key={h} style={{ ...thStyle, padding: '8px 10px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportModal.assessments.map(a => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ ...mutedTdStyle, padding: '8px 10px' }}>{a.id}</td>
                      <td style={{ ...tdStyle, padding: '8px 10px' }}>{a.mode === 'enterprise' ? '企业' : '个人'}</td>
                      <td style={{ ...tdStyle, padding: '8px 10px' }}>{DEPTH_LABEL[a.depth] || a.depth}</td>
                      <td style={{ ...tdStyle, padding: '8px 10px', color: a.status === 'completed' ? '#16a34a' : '#94a3b8' }}>
                        {STATUS_LABEL[a.status] || a.status}
                      </td>
                      <td style={{ ...tdStyle, padding: '8px 10px', fontWeight: 600 }}>{a.total_score != null ? a.total_score : '-'}</td>
                      <td style={{ ...tdStyle, padding: '8px 10px' }}>{a.grade || '-'}</td>
                      <td style={{ ...mutedTdStyle, padding: '8px 10px' }}>
                        {a.completed_at ? new Date(a.completed_at).toLocaleDateString('zh-CN') : '-'}
                      </td>
                      <td style={{ ...tdStyle, padding: '8px 10px' }}>
                        {a.status === 'completed' && a.report_status === 'done' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <button
                              onClick={() => { setReportModal(null); navigate(`/report/v5/${a.id}`); }}
                              style={smallBlueButtonStyle}
                            >
                              用户版（基础）
                            </button>
                            <button
                              onClick={() => { setReportModal(null); navigate(`/report/v5/${a.id}/detailed`); }}
                              style={smallIndigoButtonStyle}
                            >
                              用户版（专业）
                            </button>
                            <button
                              onClick={() => { setReportModal(null); navigate(`/admin/reports/${a.id}`); }}
                              style={smallGreenButtonStyle}
                            >
                              后台详细报告
                            </button>
                          </div>
                        )}
                        {a.status === 'completed' && a.report_status === 'failed' && (
                          <span style={{ fontSize: 12, color: '#dc2626' }}>生成失败</span>
                        )}
                        {a.status === 'completed' && !a.report_status && (
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>生成中</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

// ── 报告弹窗内「重新生成」子面板：选第几次测评 + 选基础版/专业版 ──
function RegenPanel({ assessments, regen, setRegen, onConfirm }) {
  // 可重生的测评：已完成的（有报告可重跑）
  const eligible = assessments.filter(a => a.status === 'completed');
  const selected = eligible.find(a => a.id === regen.selId) || null;
  // 专业版只要求「基础版报告已生成」(report_status==='done')即可重生 —— 后台超管/企管
  // 是运营特权，不受用户付费解锁(is_detailed_unlocked)限制；付费门槛只对前台员工生效。
  const detailedDisabled = !selected || selected.report_status !== 'done';

  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>重新生成报告</div>
        <button onClick={() => setRegen(null)} style={secondaryButtonStyle}>← 返回</button>
      </div>

      {/* 第 1 步：选第几次测评 */}
      <div style={{ color: '#64748b', marginBottom: 6 }}>1. 选择要重生的测评：</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
        {eligible.length === 0 && (
          <div style={{ color: '#94a3b8' }}>该用户暂无已完成的测评可重生。</div>
        )}
        {eligible.map(a => {
          const active = a.id === regen.selId;
          return (
            <label
              key={a.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                border: `1.5px solid ${active ? '#2563eb' : '#e2e8f0'}`,
                background: active ? '#eff6ff' : '#fff',
                borderRadius: 8, cursor: 'pointer',
              }}
            >
              <input
                type="radio" name="regen-assessment" checked={active}
                onChange={() => setRegen(r => ({
                  ...r, selId: a.id,
                  // 选了基础版报告未就绪的测评时，把 variant 退回基础版（专业版无 base 可挂）
                  variant: a.report_status === 'done' ? r.variant : 'rough',
                  msg: '', error: '',
                }))}
              />
              <span style={{ fontWeight: 600 }}>#{a.id}</span>
              <span style={{ color: '#64748b' }}>{DEPTH_LABEL[a.depth] || a.depth}</span>
              <span style={{ color: '#64748b' }}>得分 {a.total_score != null ? a.total_score : '-'}</span>
              <span style={{ color: '#94a3b8', fontSize: 12 }}>
                {a.completed_at ? new Date(a.completed_at).toLocaleDateString('zh-CN') : '-'}
              </span>
              {a.has_detailed && (
                <span style={{ fontSize: 11, color: '#16a34a' }}>专业版已生成</span>
              )}
            </label>
          );
        })}
      </div>

      {/* 第 2 步：选基础版/专业版 */}
      <div style={{ color: '#64748b', marginBottom: 6 }}>2. 选择重生版本：</div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
        <label style={regenVariantLabel(regen.variant === 'rough')}>
          <input
            type="radio" name="regen-variant" checked={regen.variant === 'rough'}
            onChange={() => setRegen(r => ({ ...r, variant: 'rough', msg: '', error: '' }))}
          />
          <div>
            <div style={{ fontWeight: 600 }}>基础版</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>只重跑基础版，保留专业版</div>
          </div>
        </label>
        <label style={{ ...regenVariantLabel(regen.variant === 'detailed'), opacity: detailedDisabled ? 0.5 : 1, cursor: detailedDisabled ? 'not-allowed' : 'pointer' }}>
          <input
            type="radio" name="regen-variant" checked={regen.variant === 'detailed'}
            disabled={detailedDisabled}
            onChange={() => setRegen(r => ({ ...r, variant: 'detailed', msg: '', error: '' }))}
          />
          <div>
            <div style={{ fontWeight: 600 }}>专业版</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              {detailedDisabled ? '基础版报告未就绪，无法重生专业版' : '只重跑深度内容，保留基础版'}
            </div>
          </div>
        </label>
      </div>

      {regen.error && <div style={{ color: '#dc2626', margin: '8px 0' }}>{regen.error}</div>}
      {regen.msg && <div style={{ color: '#16a34a', margin: '8px 0' }}>{regen.msg}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
        <button
          onClick={onConfirm}
          disabled={!regen.selId || regen.busy}
          style={{
            ...regenEntryButtonStyle,
            opacity: (!regen.selId || regen.busy) ? 0.5 : 1,
            cursor: (!regen.selId || regen.busy) ? 'default' : 'pointer',
          }}
        >
          {regen.busy ? '触发中…' : '确认重新生成'}
        </button>
      </div>
    </div>
  );
}

function regenVariantLabel(active) {
  return {
    flex: 1, display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px',
    border: `1.5px solid ${active ? '#2563eb' : '#e2e8f0'}`,
    background: active ? '#eff6ff' : '#fff', borderRadius: 8, cursor: 'pointer',
  };
}

const regenEntryButtonStyle = {
  padding: '7px 16px',
  borderRadius: 8,
  border: '1.5px solid #2563eb',
  background: '#2563eb',
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const inputInlineStyle = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 13,
  minWidth: 220,
};
const selectInlineStyle = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 13,
};
const secondaryButtonStyle = {
  padding: '7px 14px',
  borderRadius: 8,
  fontSize: 13,
  cursor: 'pointer',
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#64748b',
  fontWeight: 500,
};
const thStyle = {
  padding: '12px 14px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#475569',
  borderBottom: '1px solid #e2e8f0',
};
const tdStyle = { padding: '10px 14px', verticalAlign: 'middle' };
const mutedTdStyle = { ...tdStyle, color: '#94a3b8', fontSize: 12 };
const emptyCellStyle = { padding: 32, textAlign: 'center', color: '#94a3b8' };
const smallBlueButtonStyle = {
  padding: '4px 10px',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 500,
  border: '1px solid #2563eb30',
  background: '#eff6ff',
  color: '#2563eb',
  whiteSpace: 'nowrap',
};
const smallGreenButtonStyle = {
  ...smallBlueButtonStyle,
  border: '1px solid #16a34a30',
  background: '#f0fdf4',
  color: '#16a34a',
};
const smallIndigoButtonStyle = {
  ...smallBlueButtonStyle,
  border: '1px solid #6366f130',
  background: '#eef2ff',
  color: '#4f46e5',
};
const smallNeutralButtonStyle = {
  ...smallBlueButtonStyle,
  border: '1px solid #94a3b830',
  background: '#f8fafc',
  color: '#64748b',
};
const pagerButtonStyle = {
  padding: '6px 14px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: 13,
  background: '#fff',
};
const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};
const modalCardStyle = {
  background: '#fff',
  borderRadius: 12,
  padding: '24px 28px',
  minWidth: 520,
  maxWidth: 640,
  maxHeight: '84vh',
  overflow: 'auto',
  boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
};
const closeButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: 18,
  cursor: 'pointer',
  color: '#94a3b8',
};
const summaryBoxStyle = {
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  borderRadius: 8,
  padding: '12px 14px',
  marginBottom: 16,
};
const fieldGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
};
const checkRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: '#334155',
  fontSize: 13,
};
const labelStyle = {
  display: 'block',
  marginBottom: 6,
  color: '#475569',
  fontSize: 12,
  fontWeight: 600,
};
const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 13,
  boxSizing: 'border-box',
};
const noticeStyle = {
  border: '1px solid #bfdbfe',
  background: '#eff6ff',
  color: '#1d4ed8',
  borderRadius: 8,
  padding: '12px 14px',
  fontSize: 13,
  lineHeight: 1.6,
  marginTop: 16,
};
const dangerBoxStyle = {
  marginTop: 20,
  border: '1px solid #fecaca',
  background: '#fef2f2',
  borderRadius: 8,
  padding: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};
const modalFooterStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  marginTop: 22,
};
